import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import "./ForYou.scss";
import AssetPlayer from "../player/AssetPlayer";
import CirclesLoader from "../../components/loader/CirclesLoader";
import PersonalizationLoader from "../../components/loader/PersonalizationLoader";
import { apiGetAllAssets } from "../../graphQL/asset";
import { useOnCreateAsset, useOnUpdateAsset, useOnDeleteAsset } from "../../graphQL/asset/subscription";
import { useAppContext } from "../../contexts/AppContext";
import { parseDomainAndLocation } from "../../utils";

function PageForYou() {
  // const allAssets = useSelector((store) => store.Asset.assets);
  const categories = useSelector((store) => store.Category.categories);
  const domains = useSelector((store) => store.Domain.domains);
  const PrivateInfo = useSelector((store) => store.auth.PrivateInfo);
  const OtherProfile = useSelector((store) => store.OtherProfile.Profile);

  const [profile, setProfile] = useState();
  const [assets, setAssets] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [categoryState, setCategoryState] = useState({
    order: [],
    assetsById: {},
    totalsById: {},
    pageById: {},
  });
  const [splashDone, setSplashDone] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSplashDone(true), 10000);
    return () => clearTimeout(timer);
  }, []);

  const [filter, setFilter] = useState({
    domain: PrivateInfo?.name,
    pagination: {
      page: 1,
      limit: 99,
    },
    status: "Published",
    highlight: true,
    // categories: null,
  });
  
  const categoryLoadingRef = useRef({});
  const lastProcessedCreateRef = useRef(null);
  const lastProcessedUpdateRef = useRef(null);
  const lastProcessedDeleteRef = useRef(null);

  const { data: createAsset } = useOnCreateAsset();
  const { data: updateAsset } = useOnUpdateAsset();
  const { data: deleteAsset } = useOnDeleteAsset();

  useEffect(() => {
    if ((PrivateInfo || OtherProfile) && domains) {
      if (OtherProfile) {
        setProfile(OtherProfile);
      } else {
        setProfile(PrivateInfo);
      }
      // Use URL domain instead of user's domain, unless viewing another profile
      const { domain: urlDomain } = parseDomainAndLocation();
      const domainName = OtherProfile?.domain || urlDomain;
      const domain = domains.find((d) => d.name === domainName);
      setFilter({
        ...filter,
        domain: domainName,
        pagination: {
          page: 1,
          limit: domain?.fetchCount || 99,
        }
      })
    }
  }, [PrivateInfo, OtherProfile, domains]);

  useEffect(() => {
    if (!profile || !categories?.length || !filter?.domain) return;

    const run = async () => {
      setIsLoading(true);
      setAssets(null);
      categoryLoadingRef.current = {};

      try {
        const domainCategories = categories.filter((cat) => cat.domain === filter.domain);
        const order = domainCategories;
        const categorySet = new Set(order.map((c) => c.id));
        const pageSize = filter.pagination?.limit || 99;

        // Single call to load all published assets for this domain
        const data = await apiGetAllAssets({
          ...filter,
          pagination: { page: 1, limit: pageSize },
        });
        const allAssets = data?.mediaAssets || [];

        // Bucket assets by their first matching category
        const assetsById = {};
        const totalsById = {};
        const pageById = {};
        for (const cat of order) {
          assetsById[cat.id] = [];
          totalsById[cat.id] = 0;
          pageById[cat.id] = 1;
        }
        for (const asset of allAssets) {
          const catId = (asset.categories || []).find((id) => categorySet.has(id));
          if (catId) {
            assetsById[catId].push(asset);
            totalsById[catId] += 1;
          }
        }

        setCategoryState({
          order,
          assetsById,
          totalsById,
          pageById,
        });
      } finally {
        setIsLoading(false);
      }
    };

    run();
  }, [profile, categories, filter?.domain, filter?.pagination?.limit, filter?.status, filter?.highlight]);

  // Subscription: onCreateMediaAsset — add matching asset to category lists
  useEffect(() => {
    if (!createAsset?.onCreateMediaAsset?.mediaAsset || !filter?.domain) return;
    const asset = createAsset.onCreateMediaAsset.mediaAsset;
    const eventKey = `create:${asset.id}`;
    if (lastProcessedCreateRef.current === eventKey) return;
    lastProcessedCreateRef.current = eventKey;

    const matchesFilter =
      asset.domain === filter.domain &&
      asset.status === "Published" &&
      (filter.highlight == null || filter.highlight === asset.highlight);
    if (!matchesFilter) return;

    setCategoryState((prev) => {
      if (!prev.order?.length) return prev;
      const categoryIds = Array.isArray(asset.categories) ? asset.categories : [];
      const relevantIds = categoryIds.filter((id) => prev.order.some((c) => c.id === id));
      if (!relevantIds.length) return prev;

      const nextAssetsById = { ...prev.assetsById };
      const nextTotalsById = { ...prev.totalsById };
      for (const categoryId of relevantIds) {
        const list = nextAssetsById[categoryId] || [];
        if (list.some((a) => a.id === asset.id)) return prev;
        nextAssetsById[categoryId] = [asset, ...list];
        nextTotalsById[categoryId] = (nextTotalsById[categoryId] ?? 0) + 1;
      }
      return { ...prev, assetsById: nextAssetsById, totalsById: nextTotalsById };
    });
  }, [createAsset, filter?.domain, filter?.highlight]);

  // Subscription: onUpdateMediaAsset — keep ForYou list stable while video is playing
  useEffect(() => {
    if (!updateAsset?.onUpdateMediaAsset?.mediaAsset || !filter?.domain) return;
    const asset = updateAsset.onUpdateMediaAsset.mediaAsset;
    const eventKey = `update:${asset.id}:${asset.updatedAt ?? asset.id}`;
    if (lastProcessedUpdateRef.current === eventKey) return;
    lastProcessedUpdateRef.current = eventKey;
  }, [updateAsset, filter?.domain, filter?.highlight]);

  // Subscription: onDeleteMediaAsset — remove asset from all category lists
  useEffect(() => {
    if (!deleteAsset?.onDeleteMediaAsset?.id) return;
    const deletedId = deleteAsset.onDeleteMediaAsset.id;
    const eventKey = `delete:${deletedId}`;
    if (lastProcessedDeleteRef.current === eventKey) return;
    lastProcessedDeleteRef.current = eventKey;

    setCategoryState((prev) => {
      if (!prev.order?.length) return prev;
      let changed = false;
      const nextAssetsById = { ...prev.assetsById };
      const nextTotalsById = { ...prev.totalsById };
      for (const cat of prev.order) {
        const list = nextAssetsById[cat.id] || [];
        if (!list.some((a) => a.id === deletedId)) continue;
        nextAssetsById[cat.id] = list.filter((a) => a.id !== deletedId);
        nextTotalsById[cat.id] = Math.max(0, (nextTotalsById[cat.id] ?? 0) - 1);
        changed = true;
      }
      return changed ? { ...prev, assetsById: nextAssetsById, totalsById: nextTotalsById } : prev;
    });
  }, [deleteAsset]);

  useEffect(() => {
    if (!categoryState.order.length) {
      if (!isLoading) {
        setAssets([]);
      }
      return;
    }
    const orderedAssets = categoryState.order.reduce((acc, cat) => {
      const catAssets = categoryState.assetsById[cat.id] || [];
      return acc.concat(catAssets);
    }, []);
    setAssets(orderedAssets);
  }, [categoryState, isLoading]);

  // Load more videos for a specific category
  const handleLoadMore = useCallback(
    async ({ categoryId } = {}) => {
      if (!categoryId) return;
      if (categoryLoadingRef.current[categoryId]) return;

      const total = categoryState.totalsById[categoryId] || 0;
      const loaded = categoryState.assetsById[categoryId]?.length || 0;
      if (!total || loaded >= total) return;

      const currentPage = categoryState.pageById[categoryId] || 0;
      const pageSize = filter.pagination?.limit || 5;
      const nextPage = currentPage === 0 ? 1 : currentPage + 1;

      categoryLoadingRef.current[categoryId] = true;
      try {
        const data = await apiGetAllAssets({
          ...filter,
          categories: [categoryId],
          pagination: { page: nextPage, limit: pageSize },
        });

        const newAssets = data?.mediaAssets || [];
        setCategoryState((prev) => {
          const prevAssets = prev.assetsById[categoryId] || [];
          const mergedAssets = nextPage === 1 ? newAssets : [...prevAssets, ...newAssets];
          return {
            ...prev,
            assetsById: { ...prev.assetsById, [categoryId]: mergedAssets },
            totalsById: { ...prev.totalsById, [categoryId]: data?.total || prev.totalsById[categoryId] || 0 },
            pageById: { ...prev.pageById, [categoryId]: nextPage },
          };
        });
      } catch (error) {
        console.error(`Error loading more assets for category ${categoryId}:`, error);
      } finally {
        categoryLoadingRef.current[categoryId] = false;
      }
    },
    [categoryState, filter],
  );

  if (!splashDone || isLoading || assets == null)
    return (
      <Box sx={{ width: "100%", height: "100%" }}>
        <PersonalizationLoader />
      </Box>
    );

/*  if (assets?.length < 1) {
    return <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: "100%",
        height: "100%",
      }}
    >
      <Typography variant="h5" color="common.white">
        No asset available at this time
      </Typography>
    </Box>;
  }*/

  return (
    <Box className="foryou-player-wrapper">
      <AssetPlayer assets={assets} filter={filter} setFilter={setFilter} onLoadMore={handleLoadMore}></AssetPlayer>
    </Box>
  );
}

export default PageForYou;
